import React from "react";
import { useToast } from "../components/ResultsPrimitives.jsx";
import useAccess from "./useAccess.jsx";
import {
  fetchMyNotifications,
  markAllNotificationsRead,
  markNotificationsSeen,
  updateNotificationRead,
} from "../lib/automationsApi.js";

const NotificationContext = React.createContext(null);
const POLL_INTERVAL_MS = 15_000;
const MAX_TOASTS_PER_POLL = 3;

function toastTypeForSeverity(severity) {
  if (severity === "error") {
    return "error";
  }
  if (severity === "success") {
    return "success";
  }
  return "info";
}

export function NotificationProvider({ children }) {
  const { access, isAuthError, isReady, session } = useAccess();
  const { dismissToast, showToast } = useToast();
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const displayedNotificationIdsRef = React.useRef(new Set());
  const notificationToastIdsRef = React.useRef(new Set());
  const requestInFlightRef = React.useRef(null);
  const sessionUserId = typeof session?.user?.id === "string" ? session.user.id.trim() : "";
  const accessUserId = typeof access?.userId === "string" ? access.userId.trim() : "";
  const notificationUserId = sessionUserId
    ? (accessUserId === sessionUserId ? sessionUserId : "")
    : (access?.source === "local_bypass" ? accessUserId : "");
  const sessionAccessToken = typeof session?.access_token === "string" ? session.access_token : "";
  const notificationIdentity = notificationUserId
    ? `${notificationUserId}:${sessionAccessToken || "local-bypass"}`
    : "";
  const identityRef = React.useRef(notificationIdentity);
  identityRef.current = notificationIdentity;

  const refresh = React.useCallback(async ({ showNewToasts = true } = {}) => {
    if (!isReady || isAuthError || !notificationIdentity) {
      return null;
    }
    if (requestInFlightRef.current?.identity === notificationIdentity) {
      return null;
    }
    requestInFlightRef.current?.controller?.abort();
    const controller = new AbortController();
    const requestIdentity = notificationIdentity;
    requestInFlightRef.current = { controller, identity: requestIdentity };
    setIsLoading(true);
    try {
      const payload = await fetchMyNotifications({ limit: 50, signal: controller.signal });
      if (controller.signal.aborted || identityRef.current !== requestIdentity) {
        return null;
      }
      const nextNotifications = Array.isArray(payload?.notifications) ? payload.notifications : [];
      setNotifications(nextNotifications);
      setUnreadCount(Number(payload?.unread_count) || 0);
      setErrorMessage("");

      const unseen = nextNotifications.filter((notification) => (
        !notification.seen_at
        && !displayedNotificationIdsRef.current.has(notification.id)
      ));
      unseen.forEach((notification) => displayedNotificationIdsRef.current.add(notification.id));
      if (showNewToasts) {
        unseen.slice(0, MAX_TOASTS_PER_POLL).reverse().forEach((notification) => {
          const toastId = showToast({
            title: notification.title,
            message: notification.body,
            type: toastTypeForSeverity(notification.severity),
            duration: null,
            dismissOnAction: false,
            actionLabel: notification.action_url ? "Ouvrir" : undefined,
            onAction: notification.action_url
              ? () => {
                  if (identityRef.current !== requestIdentity) {
                    return;
                  }
                  void updateNotificationRead(notification.id, true).finally(() => {
                    if (identityRef.current === requestIdentity) {
                      window.location.assign(notification.action_url);
                    }
                  });
                }
              : undefined,
          });
          notificationToastIdsRef.current.add(toastId);
        });
      }
      if (unseen.length > 0) {
        const seenAt = new Date().toISOString();
        setNotifications((current) => current.map((notification) => (
          unseen.some((item) => item.id === notification.id)
            ? { ...notification, seen_at: notification.seen_at || seenAt }
            : notification
        )));
        void markNotificationsSeen(unseen.map((notification) => notification.id)).catch(() => {});
      }
      return payload;
    } catch (error) {
      if (controller.signal.aborted || identityRef.current !== requestIdentity || error?.name === "AbortError") {
        return null;
      }
      setErrorMessage(error instanceof Error ? error.message : "Impossible de charger les notifications.");
      return null;
    } finally {
      if (requestInFlightRef.current?.controller === controller) {
        requestInFlightRef.current = null;
      }
      if (identityRef.current === requestIdentity) {
        setIsLoading(false);
      }
    }
  }, [isAuthError, isReady, notificationIdentity, showToast]);

  React.useEffect(() => {
    requestInFlightRef.current?.controller?.abort();
    requestInFlightRef.current = null;
    notificationToastIdsRef.current.forEach((toastId) => dismissToast(toastId));
    notificationToastIdsRef.current.clear();
    displayedNotificationIdsRef.current.clear();
    setNotifications([]);
    setUnreadCount(0);
    setErrorMessage("");
    setIsLoading(false);
    if (!isReady || isAuthError || !notificationIdentity) return undefined;
    void refresh();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    }, POLL_INTERVAL_MS);
    const handleFocus = () => void refresh();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      requestInFlightRef.current?.controller?.abort();
      requestInFlightRef.current = null;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [dismissToast, isAuthError, isReady, notificationIdentity, refresh]);

  const markRead = React.useCallback(async (notificationId, read = true) => {
    const requestIdentity = notificationIdentity;
    const updated = await updateNotificationRead(notificationId, read);
    if (!requestIdentity || identityRef.current !== requestIdentity) {
      return updated;
    }
    const previous = notifications.find((notification) => notification.id === notificationId);
    const wasUnread = previous ? !previous.read_at : null;
    const isUnread = !updated.read_at;
    setNotifications((current) => current.map((notification) => (
      notification.id === notificationId ? updated : notification
    )));
    if (wasUnread !== null && wasUnread !== isUnread) {
      setUnreadCount((count) => Math.max(0, count + (isUnread ? 1 : -1)));
    }
    return updated;
  }, [notificationIdentity, notifications]);

	const markAllRead = React.useCallback(async () => {
		const requestIdentity = notificationIdentity;
		await markAllNotificationsRead({ authAccessToken: sessionAccessToken || null });
    if (!requestIdentity || identityRef.current !== requestIdentity) {
      return;
    }
    const now = new Date().toISOString();
    setNotifications((current) => current.map((notification) => ({
      ...notification,
      seen_at: notification.seen_at || now,
      read_at: notification.read_at || now,
    })));
    setUnreadCount(0);
	}, [notificationIdentity, sessionAccessToken]);

  const value = React.useMemo(() => ({
    errorMessage,
    isLoading,
    markAllRead,
    markRead,
    notifications,
    refresh,
    unreadCount,
  }), [errorMessage, isLoading, markAllRead, markRead, notifications, refresh, unreadCount]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider.");
  }
  return context;
}
